/**
 * KPI-PILL-001 (Sep 2026) — "Option 2 – Single Row Pill Style".
 * One horizontal white pill holding every KPI (icon + label + value) with
 * subtle separators; wraps on narrow screens. Approved for the Admin / Tax TL /
 * Title TL Operations dashboards and the Processor dashboards ONLY.
 * Presentation only — callers own the metrics, visibility rules and data.
 */
import React from 'react';

export const KPI_TONES = {
  blue:    { bg: '#DBEAFE', fg: '#2563EB' },
  emerald: { bg: '#D1FAE5', fg: '#059669' },
  amber:   { bg: '#FEF3C7', fg: '#D97706' },
  violet:  { bg: '#EDE9FE', fg: '#7C3AED' },
  rose:    { bg: '#FFE4E6', fg: '#E11D48' },
  sky:     { bg: '#E0F2FE', fg: '#0284C7' },
  slate:   { bg: '#F1F5F9', fg: '#475569' },
};

// Metric key → tone, shared by both approved dashboards so the same metric
// always carries the same colour regardless of role.
export const KPI_KEY_TONES = {
  total: 'blue',
  open: 'emerald',
  pending: 'amber',
  mailaway: 'violet',
  qc_review: 'rose',
  mailaway_received: 'sky',
  clarification: 'violet',
  clarification_received: 'emerald',
  returned_for_correction: 'rose',
};

export const KpiValueSkeleton = () => (
  <span className="inline-block h-5 w-9 rounded-md bg-slate-200 animate-pulse align-middle"
    data-testid="kpi-value-skeleton" aria-hidden="true" />
);

const toneStyle = (tone, accentColor) => {
  if (accentColor) return { background: `${accentColor}1F`, color: accentColor };
  const t = KPI_TONES[tone] || KPI_TONES.blue;
  return { background: t.bg, color: t.fg };
};

export const KpiPillItem = ({ icon: Icon, label, value, tone, accentColor, loading, renderValue, testId, ariaLabel }) => (
  <div
    className="kpi-pill-item flex flex-1 basis-[140px] min-w-[140px] items-center gap-2.5 px-3 py-1 border-l border-slate-200/80 first:border-l-0"
    role="group"
    tabIndex={0}
    aria-label={ariaLabel || (loading ? `${label}: loading` : `${label}: ${value}`)}
    data-testid={testId || `kpi-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full" style={toneStyle(tone, accentColor)}>
      {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
    </span>
    <span className="min-w-0 leading-tight">
      <span className="block text-[11px] font-medium leading-[1.15] text-slate-500">{label}</span>
      <span className="block text-lg font-bold tabular-nums tracking-tight text-slate-900">
        {loading ? <KpiValueSkeleton /> : (renderValue ? renderValue(value) : value)}
      </span>
    </span>
  </div>
);

export const KpiPillRow = ({ items, loading, stale, renderValue, testId, className = '' }) => (
  <div
    className={`kpi-pill-row flex flex-wrap items-stretch rounded-2xl border border-slate-200 bg-white px-1 py-2 shadow-[0_6px_18px_rgba(17,24,39,0.06)]${stale ? ' opacity-60' : ''} ${className}`}
    data-testid={testId}
  >
    {items.map((it) => (
      <KpiPillItem key={it.key} icon={it.icon} label={it.label} value={it.value}
        tone={it.tone || KPI_KEY_TONES[it.key]} accentColor={it.accentColor}
        loading={loading} renderValue={renderValue} testId={it.testId} />
    ))}
  </div>
);

export default KpiPillRow;
